import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons/core-free-icons";
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * MetricCell — Single metric with label, value, and optional source info.
 * Inspired by Stripe's editorial metric rows.
 */
export function MetricCell({ label, value, unit, trend, source, period, isDemo, className }) {
  return (
    <div
      className={cn('flex flex-col gap-1 px-4 py-3 border-r border-border last:border-r-0', className)}
      role="group"
      aria-label={label}>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        {(source || period) && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  aria-label={`Información sobre ${label}`}>
                  <HugeiconsIcon icon={InformationCircleIcon} className="h-3 w-3" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs max-w-48">
                {source && <p>Fuente: {source}</p>}
                {period && <p>Período: {period}</p>}
                {isDemo && <p className="text-warning mt-1">Datos de demostración</p>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {isDemo && (
          <span className="h-1.5 w-1.5 rounded-full bg-warning animate-pulse-slow" aria-label="Dato demostrativo" />
        )}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-2xl font-bold tabular text-foreground">{value ?? '—'}</span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </div>
      {trend !== undefined && trend !== null && (
        <span
          className={cn(
            'text-[11px] font-medium',
            trend > 0 ? 'text-success' : trend < 0 ? 'text-risk-high' : 'text-muted-foreground',
          )}>
          {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend)}% vs período anterior
        </span>
      )}
    </div>
  );
}

/**
 * MetricStrip — Horizontal row of metrics separated by fine lines.
 * Inspired by Stripe/Watershed metric bars.
 */
export function MetricStrip({ children, className }) {
  return (
    <div
      className={cn(
        'flex flex-wrap rounded-xl border border-border bg-card shadow-card divide-x divide-border overflow-hidden',
        className,
      )}
      role="region"
      aria-label="Métricas principales">
      {children}
    </div>
  );
}
